import { formatMoney } from '../../lib/formatters.ts'

export type ConclusionKind = 'covered' | 'watch_later' | 'tight' | 'below_zero'

export type ForecastWeekFlags = {
  week_start: string
  close_cents?: number
  below_zero?: boolean
  below_cushion?: boolean
}

export type ForecastVersionLike = {
  payload?: {
    weeks?: ForecastWeekFlags[]
    action_count?: number
  } | null
}

export type Conclusion = {
  kind: ConclusionKind
  sentence: string
  watchWeekStart?: string
}

function monthDay(iso: string): string {
  const [y, m, d] = String(iso).split('-').map(Number)
  if (!y || !m || !d) return iso
  return new Date(y, m - 1, d).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
  })
}

function isDip(w?: ForecastWeekFlags | null): boolean {
  return !!(w && (w.below_zero || w.below_cushion))
}

/** n=0 → ''; n=1 → ' 1 action below.'; n≥2 → ' N actions below.' */
export function formatActionClause(n: unknown): string {
  const count = Math.max(0, Math.floor(Number(n) || 0))
  if (count < 1) return ''
  if (count === 1) return ' 1 action below.'
  return ` ${count} actions below.`
}

export function selectConclusion(version: ForecastVersionLike | null | undefined): Conclusion {
  const weeks = version?.payload?.weeks || []
  const zero = weeks.find((w) => w.below_zero)
  if (zero) {
    return {
      kind: 'below_zero',
      sentence: `Cash may go below ${formatMoney(0)} the week of ${monthDay(zero.week_start)}.`,
      watchWeekStart: zero.week_start,
    }
  }

  const thisWeek = weeks[0]
  const nextWeek = weeks[1]
  if (isDip(thisWeek) || isDip(nextWeek)) {
    const hit = isDip(thisWeek) ? thisWeek : nextWeek
    const n = Number(version?.payload?.action_count) || 0
    return {
      kind: 'tight',
      sentence: `Cash is tight the week of ${monthDay(hit!.week_start)}.${formatActionClause(n)}`,
      watchWeekStart: hit!.week_start,
    }
  }

  const later = weeks.slice(2).find((w) => w.below_cushion)
  if (later) {
    return {
      kind: 'watch_later',
      sentence: `You are covered this week and next. Watch the week of ${monthDay(later.week_start)}.`,
      watchWeekStart: later.week_start,
    }
  }

  return { kind: 'covered', sentence: 'You are covered for the next 8 weeks.' }
}
